// The policy in plain words: the one-line sentence under the editor and on
// the detail page (spec 5), and the detail page's rows (label left, value
// right, one text style).
import React from "react";

import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { conditionClauses, conditionRows } from "../../../components/policyShell/conditionsModel";
import {
  actionReviewText,
  approvalReviewText,
  policySentence,
  sessionReviewText,
  whoReviewText,
  whoSentenceText,
} from "../../../components/policyShell/policyModel";

/**
 * "Require MFA for sathyam@authnull.com on dbmfa_test as dbmfa_reader,
 *  outside Mon–Fri 09:00–18:00 (Asia/Kolkata), except breakglass@corp.com."
 */
export function policySummarySentence(adapter, draft, data) {
  const subject = adapter.labels?.subject || "sign-in";
  const everyone = !!(adapter.who?.everyone && draft.who?.everyone);
  const lists = (adapter.who?.fields || []).map((f) => ({ kind: f.kind || f.key, values: draft.who?.[f.key] || [] }));
  return policySentence({
    flow: draft.action,
    who: whoSentenceText({ everyone, lists }),
    where: (adapter.sentenceWhere && adapter.sentenceWhere(draft, data)) || "",
    clauses: conditionClauses(draft.conditions, { subject, product: adapter.key }),
    except: [...(draft.except?.users || []), ...(draft.except?.groups || [])],
    fallback: !!(adapter.features?.fallback && draft.fallback),
  });
}

/** [{ key, label, value }] for a draft (the detail page). */
export function summaryRows(adapter, draft, data) {
  const subject = adapter.labels?.subject || "sign-in";
  const fields = adapter.who?.fields || [];
  const everyone = !!(adapter.who?.everyone && draft.who?.everyone);
  const lists = fields.map((f) => ({ label: f.label, values: draft.who?.[f.key] || [] })).filter((l) => l.values.length);
  const except = [...(draft.except?.users || []), ...(draft.except?.groups || [])];
  // One line per kind ("Groups: Finance"), so a label never reads as part of a name.
  const whoText =
    everyone || !lists.length ? (
      whoReviewText({ everyone, lists, except })
    ) : (
      <div style={{ display: "grid", gap: "0.25rem" }}>
        {lists.map((l) => (
          <div key={l.label}>
            <span style={{ color: AN_COLORS.muted }}>{l.label}: </span>
            {l.values.join(", ")}
          </div>
        ))}
        {except.length > 0 && (
          <div>
            <span style={{ color: AN_COLORS.muted }}>Except: </span>
            {except.join(", ")}
          </div>
        )}
      </div>
    );
  const product = (adapter.reviewRows && adapter.reviewRows(draft, data)) || [];
  const conds = conditionRows(draft.conditions, { subject, product: adapter.key });
  const rows = [
    { key: "who", label: "Who", value: whoText },
    ...product.filter((r) => (r.section || "where") === "where"),
    {
      key: "conditions",
      label: "Conditions",
      value: conds.length ? (
        <div style={{ display: "grid", gap: "0.25rem" }}>
          {conds.map((c) => (
            <div key={c.key} style={c.hidden ? { color: AN_COLORS.muted } : undefined}>
              {c.hidden ? c.summary : `${c.label}: ${c.summary}`}
            </div>
          ))}
        </div>
      ) : (
        `None. Applies to every ${subject} in scope.`
      ),
    },
    { key: "action", label: "Action", value: actionReviewText(draft.action, adapter.features?.fallback && draft.fallback) },
    ...product.filter((r) => r.section === "action"),
  ];
  // SSH only: the other products don't have Approval or Session.
  if (adapter.features?.approval && draft.action !== "block") {
    rows.push({ key: "approval", label: "Approval", value: approvalReviewText(draft.approval, { subject }) });
  }
  if (adapter.features?.session) {
    const withRecording = adapter.features.session === true || !!adapter.features.session.record;
    rows.push({ key: "session", label: "Session", value: sessionReviewText(draft.session, { recording: withRecording }) });
  }
  return rows;
}

export default function PolicySummary({ rows }) {
  return (
    <dl style={{ margin: 0, fontFamily: AN_FONT }}>
      {rows.map((r, i) => (
        <div
          key={r.key}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(6rem, 10rem) 1fr",
            gap: "1rem",
            padding: "0.75rem 0",
            borderTop: i ? `1px solid ${AN_COLORS.line2}` : "none",
            fontSize: "0.875rem",
            lineHeight: 1.5,
          }}
        >
          <dt style={{ color: AN_COLORS.muted, margin: 0 }}>{r.label}</dt>
          <dd style={{ color: AN_COLORS.ink2, margin: 0, minWidth: 0, overflowWrap: "anywhere" }}>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}
